import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Alert } from 'react-bootstrap';
import Nestable from 'react-nestable';

import SharePointFolderBrowser from '../components/psc/lib/SharePointFolderBrowser/SharePointFolderBrowser';
import SaveButton from '../components/psc/lib/SaveButton';
import CancelButton from '../components/psc/lib/CancelButton';
import ErrorMessage from '../components/psc/lib/ErrorMessage';

import { setProject } from '../obj/pscState'

import useAuthentication from '../hooks/useAuthentication';
import useSharepointToken from '../hooks/useSharepointToken';
import useUserManagement from '../hooks/useUserManagement';
import useGncHistory from '../hooks/useGncHistory';
import useProjectList from '../hooks/useProjectList';

const ProjectSharepoint = () => {
  const { tokenIsValid, redirectToAuthUrl, encryptionKey, loading } = useSharepointToken();
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [projectSystems, setProjectSystems] = useState([]);
  const [editingSystem, setEditingSystem] = useState(null);
  const [defaultSharepointFolder, setDefaultSharepointFolder] = useState(null);
  const [error, setError] = useState(null);
  const [submitSpinner, setSubmitSpinner] = useState(false);
  const [submitActive, setSubmitActive] = useState(false);
  const productTreeRef = useRef(null);

  const dispatch = useDispatch()
  const pscState = useSelector(state => state.pscState.value)
  const project = pscState.project
  const projectURL = '/v0/projects/' + project.id

  const { currentUser } = useAuthentication();

  const {
      userHasProjectAccessLevel,
  } = useUserManagement()

  const {
      goBack,
  } = useGncHistory()

  const {
    projectUpdate,
  } = useProjectList()

  useEffect(() => {
    if (project.default_sharepoint_folder) {
        setDefaultSharepointFolder(project.default_sharepoint_folder)
    }
    
    // Initialize project systems if they exist in the project data
    if (project.project_systems && project.project_systems.length > 0) {
        setProjectSystems(project.project_systems)
    }
  }, [project])
  
  const handleAuthenticate = () => {
    redirectToAuthUrl();
  };

    const _setProjectSystemSharepointFolder = (node, sharepointFolder) => {
        // I don't understand why I need to do this here??? If not, it's all "this is read-only".
        // But it works in Project.js ???
        let newNode = { ...node }
        newNode.children = node.children.map((item) => _setProjectSystemSharepointFolder(item, sharepointFolder))
        if( node.id === editingSystem.id ) {
            newNode.sharepoint_folder = sharepointFolder
        }
        return newNode
    }
    
    const handleFolderSelect = ({ sharepointFolder }) => {
        if (editingSystem) {
            // Update the specific system's sharepoint settings
            const updated = projectSystems.map((system) => _setProjectSystemSharepointFolder(system, sharepointFolder))
            setProjectSystems(updated)
            setEditingSystem(null);
        } else {
            // Update default folder
            setDefaultSharepointFolder(sharepointFolder);
        }
        setShowFolderBrowser(false);
    };
  
  const handleChangeFolder = (system) => {
    setEditingSystem(system);
    setShowFolderBrowser(true);
  };

  const handleSubmitError = (error) => {
    setError(error);
    setSubmitSpinner(false);
  };

  const renderProductTreeSystem = ({ item, collapseIcon }) => {
    const siteName = item.sharepoint_folder?.site_name || 'Not set';
    const folderPath = item.sharepoint_folder?.file_path || 'Not set';
    const locationText = `${siteName} | ${folderPath}`;
    
    return (
      <div className="product-tree-item">
        <div className="product-tree-name">
          <span className="product-tree-collapse">{collapseIcon}</span>
          <span className="product-tree-name-text">{item.name}</span>
        </div>
        <div className="product-tree-sharepoint">
          <div className="sharepoint-location">
            {locationText}
          </div>
          <button 
            type="button" 
            className="btn btn-xs btn-primary"
            onClick={() => handleChangeFolder(item)}
          >
            Change
          </button>
        </div>
      </div>
    );
  };

  const createProductTree = () => {
    return (
      <div className="product-tree-container">
        <Nestable
          threshold="25"
          disableCollapse={false}
          disableDrag={true}
          items={projectSystems}
          renderCollapseIcon={(item) => item.isCollapsed
            ? <i className="fa fa-caret-right" aria-hidden="true"></i>
            : <i className="fa fa-caret-down" aria-hidden="true"></i>
          }
          renderItem={renderProductTreeSystem}
          ref={productTreeRef}
        />
      </div>
    );
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (submitActive || !validateBeforeSubmit()) {
      return null;
    }

    setSubmitActive(true);
    setSubmitSpinner(true);
    
    // Prepare project systems data for submission
    const projectSystemsData = projectSystems.map(system => ({
      id: system.id,
      name: system.name,
      sharepoint_folder: system.sharepoint_folder
    }));

    const data = {
      default_sharepoint_folder: defaultSharepointFolder,
      project_systems: projectSystemsData
    }
    
    fetch(projectURL, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json()
            }
            throw new Error('Network response was not ok.');
        }).then(json => {
            projectUpdate(json.result.id, json.result)
            dispatch(setProject(json.result))
            goBack()
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error.message);
        });
    return null
  };

  const validateBeforeSubmit = () => {
    let error = []
    if ( ! defaultSharepointFolder ) {
        error.push("Default Sharepoint Location is required")
    }
    if ( error.length > 0 ) {
        setError(error)
        return false
    }
    return true
}

  if (loading) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-content">
                <div>Loading token status...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenIsValid) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title">
                <h2>SharePoint Integration</h2>
              </div>
              <div className="ibox-content">
                <Alert variant="warning">
                    On this page, you configure which Sharepoint folders newly created documents shall be uploaded to.
                    In order to set up this configuration, you need to authenticate with Sharepoint.
                </Alert>
                <button 
                  className="btn btn-primary" 
                  onClick={handleAuthenticate}
                >
                  Authenticate via Sharepoint
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper wrapper-content">
      <div className="row">
        <div className="col-lg-12">
          <div className="ibox float-e-margins">
            <div className="ibox-title">
              <h2>SharePoint Integration</h2>
            </div>
            <div className="ibox-content sharepoint">
              <form className="form-horizontal" onSubmit={handleSubmit}>
                  <div className="form-group">
                      <label className="col-sm-2"></label>
                      <div className="col-sm-8">
                          <p>
                              For this project, documents created in <strong>GNC Go</strong> can be uploaded to SharePoint if you configure it here.
                          </p>

                          <ul>
                              <li>
                                  Set a <strong>Default Sharepoint Location</strong> as the fallback for all documents.
                              </li>
                              <li>
                                  Optionally, assign specific folders to entries in your <strong>Product Tree</strong>.
                              </li>
                              <li>
                                  When a document is created, GNC Go uploads it to the <strong>most specific folder</strong> defined for its Applicability. If none is found, it uses the Default Location.
                              </li>
                          </ul>

                          <p>
                              This way you can keep things simple with one folder, or organize neatly across your Product Tree.
                          </p>
                          
                      </div>
                  </div>
                <div className="form-group">
                  <label className="col-sm-2 control-label required">Default Sharepoint Location</label>
                  <div className="col-sm-8" style={{ display: 'flex' }}>
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      style={{
                        whiteSpace: 'nowrap',
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        margin: 0,
                        height: '34px',
                        lineHeight: '0.5'
                      }}
                      onClick={() => setShowFolderBrowser(true)}
                    >
                      Change
                    </button>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={defaultSharepointFolder ? `${defaultSharepointFolder.site_name} | ${defaultSharepointFolder.file_path}` : ""} 
                      disabled 
                      style={{
                        flex: 1,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        marginLeft: '-1px',
                        height: '34px',
                        padding: '6px 12px'
                      }}
                    />
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group">
                  <label className="col-sm-2 control-label">Product Tree</label>
                  <div className="col-sm-8">
                    {projectSystems.length > 0 ? (
                      createProductTree()
                    ) : (
                      <div className="alert alert-info">No project systems found</div>
                    )}
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group">
                  <ErrorMessage
                    error={error}
                  />
                  <div className="col-sm-10 col-sm-offset-2">
                    <CancelButton />
                    <SaveButton
                        buttonVariant='primary'
                        visible={userHasProjectAccessLevel(currentUser,project.id, "CADM_CONTROLLER")}
                        spinnerActive={submitSpinner}
                        label='Save Changes'
                        onClick={handleSubmit}
                        onError={handleSubmitError}
                    />
                  </div>
                </div>
              </form>

              <SharePointFolderBrowser
                show={showFolderBrowser}
                onClose={() => setShowFolderBrowser(false)}
                onSelect={handleFolderSelect}
                encryptionKey={encryptionKey}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSharepoint;
